import React, { useEffect, useState } from "react";
import Movimentacao36Service from "../../services/Movimentacao36Service";
import SaldoConsolidadoService from "../../services/SaldoConsolidadoService";

/**
 * Painel de estoque do código SAP do chamado — usado no JustificarModal
 * (lado Amerinode) e no ValidarJustificativaModal (lado Vivo).
 *
 * Mostra, lado a lado:
 *   1. Movimentações dos últimos 36 meses  → o que DEVERIA haver em cada depósito
 *   2. Saldo do dia da abertura            → o que DE FATO havia
 *   3. Faixa ideal no dia                  → se havia opção melhor que a acionada
 *
 * Só aparece quando o DRS atendido difere do indicado (regra "DRS Diferente"),
 * que é o cenário em que essa comparação importa. Enquanto essa condição não é
 * satisfeita, o componente não renderiza nada e não dispara requisições.
 *
 * Props:
 *  - request (object) — Request alvo (_id, codSap, drsAtender, drsOriginal)
 */
export default function EstoqueCodSapPanel({ request }) {
    const [movimentacao, setMovimentacao] = useState(null);
    const [movBuscada, setMovBuscada] = useState(false);
    const [saldoDia, setSaldoDia] = useState(null);
    const [saldoLoading, setSaldoLoading] = useState(false);

    // A informação só interessa quando o DRS atendido difere do indicado.
    const drsDiferente = (() => {
        const a = request?.drsAtender?._id || request?.drsAtender;
        const o = request?.drsOriginal?._id || request?.drsOriginal;
        if (!a || !o) return false;
        return String(a) !== String(o);
    })();

    useEffect(() => {
        setMovimentacao(null);
        setMovBuscada(false);
        setSaldoDia(null);
        setSaldoLoading(false);

        if (!drsDiferente || !request?._id) return;
        let alive = true;

        // Movimentações (36 meses) do código SAP.
        (async () => {
            const codSap = request?.codSap;
            if (!codSap) { if (alive) setMovBuscada(true); return; }
            try {
                const data = await Movimentacao36Service.getByCodSap(codSap);
                if (alive) setMovimentacao(data?.movimentacao || null);
            } catch (e) {
                console.error("Erro ao buscar movimentações 36 meses:", e?.message || e);
            } finally {
                if (alive) setMovBuscada(true);
            }
        })();

        // Saldo consolidado do dia de abertura. A primeira consulta de um dia
        // processa a planilha no backend e pode levar alguns segundos.
        setSaldoLoading(true);
        (async () => {
            try {
                const data = await SaldoConsolidadoService.getSaldoPorRequest(request._id);
                if (alive) setSaldoDia(data);
            } catch (e) {
                console.error("Erro ao buscar saldo consolidado:", e?.message || e);
            } finally {
                if (alive) setSaldoLoading(false);
            }
        })();

        return () => { alive = false; };
    }, [request, drsDiferente]);

    const faixaLabel = (cod) => (cod === "99" ? "Dentro de Área (99)" : `Faixa ${cod}`);

    if (!drsDiferente || !movBuscada) return null;

    return (
        <div className="flex flex-col gap-2 p-3 rounded-md border border-indigo-200 bg-indigo-50">
            <div className="flex flex-row gap-4">
                <div className="flex flex-col gap-0.5 shrink-0">
                    <span className="text-[11px] font-bold text-indigo-700 uppercase">Cod Sap</span>
                    <span className="text-gray-800 font-semibold whitespace-nowrap">{request?.codSap || "—"}</span>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-bold text-indigo-700 uppercase">Movimentações 36 meses</span>
                        {movimentacao && (
                            <span className="text-[11px] font-bold text-indigo-800 bg-indigo-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                                Total: {movimentacao.total ?? 0}
                            </span>
                        )}
                    </div>
                    {movimentacao?.movDRS ? (
                        <span className="text-gray-800 font-mono text-xs break-words">{movimentacao.movDRS}</span>
                    ) : (
                        <span className="text-zinc-500 text-xs">
                            Sem registro para este código SAP na planilha atual.
                        </span>
                    )}
                </div>
            </div>

            {/* Saldo do dia de abertura do chamado */}
            <div className="flex flex-col gap-0.5 pt-2 border-t border-indigo-200">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-indigo-700 uppercase">
                        Saldo do dia {saldoDia?.data ? `(${saldoDia.data.split("-").reverse().join("/")})` : "(abertura do chamado)"}
                    </span>
                    {!saldoLoading && !saldoDia?.faltante && saldoDia && (
                        <span className="text-[11px] font-bold text-indigo-800 bg-indigo-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                            Total: {saldoDia.saldo?.total ?? 0}
                        </span>
                    )}
                </div>
                {saldoLoading ? (
                    <span className="text-zinc-500 text-xs">
                        Processando planilha do dia... isso pode levar alguns segundos.
                    </span>
                ) : saldoDia?.faltante ? (
                    <span className="text-amber-700 text-xs font-semibold">Saldo consolidado faltante</span>
                ) : saldoDia?.saldo?.saldoDRS ? (
                    <span className="text-gray-800 font-mono text-xs break-words">{saldoDia.saldo.saldoDRS}</span>
                ) : saldoDia ? (
                    <span className="text-zinc-500 text-xs">
                        Nenhuma peça disponível deste código SAP nos depósitos de SP neste dia.
                    </span>
                ) : (
                    <span className="text-zinc-500 text-xs">Saldo não disponível.</span>
                )}
            </div>

            {/* Faixa ideal: melhor faixa possível entre os depósitos que tinham
                saldo suficiente para o chamado, comparada com a praticada. */}
            {!saldoLoading && !saldoDia?.faltante && saldoDia && (
                <div className="flex flex-col gap-0.5 pt-2 border-t border-indigo-200">
                    <span className="text-[11px] font-bold text-indigo-700 uppercase">
                        Faixa ideal no dia
                        {saldoDia.quantidadeNecessaria > 1 && (
                            <span className="font-normal text-indigo-500 normal-case">
                                {" "}(para {saldoDia.quantidadeNecessaria} peças)
                            </span>
                        )}
                    </span>
                    {saldoDia.faixaIdeal ? (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                            <span className="font-bold text-gray-800">{faixaLabel(saldoDia.faixaIdeal.faixa)}</span>
                            <span className="text-gray-700">
                                &mdash; {saldoDia.faixaIdeal.depositos.map((d) => `${d.drs} (${d.quantidade})`).join(", ")}
                            </span>
                            {saldoDia.faixaPraticada && (
                                <span className={saldoDia.faixaPraticada === saldoDia.faixaIdeal.faixa ? "text-emerald-700" : "text-amber-700 font-semibold"}>
                                    · Praticada: {faixaLabel(saldoDia.faixaPraticada)}
                                    {saldoDia.drsAcionado ? ` — ${saldoDia.drsAcionado}` : ""}
                                    {saldoDia.faixaPraticada === saldoDia.faixaIdeal.faixa ? " (equivalente)" : " (havia opção melhor)"}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-zinc-500 text-xs">
                            Nenhum depósito tinha saldo suficiente ({saldoDia.quantidadeNecessaria ?? 1}) para atender o chamado neste dia.
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
